import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { getCurrentPosition } from "@/lib/geolocation";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/common";
import { MapPin, Loader2, LogIn, LogOut, Building2, Radio, AlertTriangle } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [centers, setCenters] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [lastHb, setLastHb] = useState(null); // {timestamp, inside_geofence, distance_meters}
  const hbTimerRef = useRef(null);

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const load = async () => {
    const [t, c, p] = await Promise.all([
      api.get("/attendance/today"),
      api.get("/centers"),
      api.get("/policy"),
    ]);
    setToday(t.data && Object.keys(t.data).length ? t.data : null);
    setCenters(c.data);
    setPolicy(p.data);
  };
  useEffect(() => { load(); }, []);

  const getGps = () => getCurrentPosition();

  const sendHeartbeat = async () => {
    try {
      const gps = await getGps();
      const { data } = await api.post("/attendance/heartbeat", {
        ...gps,
        device_info: navigator.userAgent.substring(0, 80),
      });
      if (data.skipped) return;
      setLastHb({
        timestamp: new Date().toISOString(),
        inside_geofence: data.inside_geofence,
        distance_meters: data.distance_meters,
        center_name: data.center_name,
      });
      if (!data.inside_geofence) {
        toast.warning(`Presence check: ${Math.round(data.distance_meters || 0)}m outside geofence`);
      }
    } catch (e) {
      // Silent on background heartbeat errors; log to console
      console.warn("heartbeat failed", e?.response?.data?.detail || e?.message);
    }
  };

  // Manage periodic heartbeat lifecycle
  useEffect(() => {
    const active = today && today.punch_in_time && !today.punch_out_time;
    const enabled = policy?.presence_check_enabled;
    const intervalMs = Math.max(60, Number(policy?.presence_check_interval_minutes || 60)) * 60_000;

    if (hbTimerRef.current) { clearInterval(hbTimerRef.current); hbTimerRef.current = null; }
    if (active && enabled) {
      // Kick off first heartbeat right away, then repeat
      sendHeartbeat();
      hbTimerRef.current = setInterval(sendHeartbeat, intervalMs);
    }
    return () => {
      if (hbTimerRef.current) { clearInterval(hbTimerRef.current); hbTimerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today?.id, today?.punch_out_time, policy?.presence_check_enabled, policy?.presence_check_interval_minutes]);

  const doPunch = async (kind) => {
    setLoading(true);
    setLoadingStep("Getting your location…");
    try {
      const gps = await getGps();
      setLoadingStep("Recording punch…");
      const device_info = navigator.userAgent.substring(0, 80);
      const { data } = await api.post(`/attendance/${kind}`, { ...gps, device_info });
      setToday(data);
      toast.success(kind === "punch-in" ? "Punched in ✓" : "Punched out ✓");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed";
      toast.error(msg);
    } finally {
      setLoading(false);
      setLoadingStep("");
    }
  };

  const stage = useMemo(() => {
    if (!today || !today.punch_in_time) return "in";
    if (!today.punch_out_time) return "out";
    return "done";
  }, [today]);

  // Live elapsed time between punch-in and (punch-out || now)
  const elapsed = useMemo(() => {
    if (!today?.punch_in_time) return null;
    const start = parseISO(today.punch_in_time).getTime();
    const end = today.punch_out_time ? parseISO(today.punch_out_time).getTime() : now.getTime();
    const diff = Math.max(0, end - start);
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1000);
    return {
      h, m, s,
      formatted: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
      running: !today.punch_out_time,
    };
  }, [today?.punch_in_time, today?.punch_out_time, now]);

  return (
    <div className="space-y-6 py-2">
      <div>
        <div className="label-mono">Today · {format(now, "EEE, d MMM yyyy")}</div>
        <div className="font-display font-black text-5xl tracking-tighter tabular-nums mt-1">
          {format(now, "HH:mm:ss")}
        </div>
      </div>

      {/* Assigned centers */}
      <Card className="rounded-none border-border p-4">
        <div className="label-mono mb-2">Assigned centers</div>
        <div className="space-y-2">
          {centers.length === 0 && <div className="text-xs text-muted-foreground">No centers assigned. Contact your admin.</div>}
          {centers.map(c => (
            <div key={c.id} className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.address}</div>
                <div className="text-[10px] font-mono-brand text-muted-foreground mt-0.5">
                  {Number(c.latitude).toFixed(4)}, {Number(c.longitude).toFixed(4)} · {c.radius_meters}m radius
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Punch card */}
      <Card className="rounded-none border-border p-6">
        <div className="flex items-center justify-between">
          <div className="label-mono">Attendance</div>
          {today?.status ? <StatusPill status={today.status} /> : <span className="label-mono">Not started</span>}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="border border-border p-3">
            <div className="label-mono">Punch in</div>
            <div className="font-mono-brand text-xl mt-1">
              {today?.punch_in_time ? format(parseISO(today.punch_in_time), "HH:mm") : "—"}
            </div>
          </div>
          <div className="border border-border p-3">
            <div className="label-mono">Punch out</div>
            <div className="font-mono-brand text-xl mt-1">
              {today?.punch_out_time ? format(parseISO(today.punch_out_time), "HH:mm") : "—"}
            </div>
          </div>
          <div className="border border-border p-3 col-span-2">
            <div className="flex items-center justify-between">
              <div className="label-mono">Working time</div>
              {elapsed?.running && (
                <span
                  data-testid="working-timer-status"
                  className="flex items-center gap-1.5 label-mono text-emerald-300"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <div
              data-testid="working-timer"
              className={`font-mono-brand tabular-nums text-3xl mt-1 tracking-tight ${
                elapsed?.running ? "text-emerald-300" : ""
              }`}
            >
              {elapsed ? elapsed.formatted : "00:00:00"}
            </div>
            <div className="label-mono text-muted-foreground mt-1">
              {stage === "done"
                ? `Final · ${today?.working_hours ?? 0} h logged`
                : elapsed?.running
                  ? "Counting since punch-in"
                  : "Waiting for punch-in"}
            </div>
          </div>
        </div>

        <div className="mt-6">
          {stage === "in" && (
            <Button
              data-testid="punch-in-btn"
              disabled={loading}
              onClick={() => doPunch("punch-in")}
              className="w-full h-16 rounded-none text-lg bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-display font-bold"
            >
              {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" /> {loadingStep || "Working…"}</> : <><LogIn className="h-5 w-5 mr-2" strokeWidth={2} /> PUNCH IN</>}
            </Button>
          )}
          {stage === "out" && (
            <Button
              data-testid="punch-out-btn"
              disabled={loading}
              onClick={() => doPunch("punch-out")}
              className="w-full h-16 rounded-none text-lg bg-red-500 hover:bg-red-400 text-red-950 font-display font-bold"
            >
              {loading ? <><Loader2 className="animate-spin h-5 w-5 mr-2" /> {loadingStep || "Working…"}</> : <><LogOut className="h-5 w-5 mr-2" strokeWidth={2} /> PUNCH OUT</>}
            </Button>
          )}
          {loading && loadingStep === "Getting your location…" && (
            <p className="text-xs text-muted-foreground text-center mt-2 label-mono" data-testid="punch-loading-hint">
              If your browser asks for permission, tap <b>Allow</b>. On iPhone Safari this can take up to 15 seconds.
            </p>
          )}
          {stage === "done" && (
            <div className="text-center py-4 border border-border">
              <div className="font-display text-lg">Day complete</div>
              <div className="text-xs text-muted-foreground">See you tomorrow, {user?.name?.split(" ")[0]}.</div>
            </div>
          )}
        </div>

        {today?.center_name && (
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Verified at {today.center_name}
          </div>
        )}

        {stage === "out" && policy?.presence_check_enabled && (
          <div
            data-testid="presence-status"
            className={`mt-4 border p-3 flex items-center justify-between text-xs ${
              lastHb == null ? "border-border text-muted-foreground"
              : lastHb.inside_geofence ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/5"
              : "border-orange-400/40 text-orange-200 bg-orange-500/5"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {lastHb && !lastHb.inside_geofence
                ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                : <Radio className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />}
              <span className="truncate">
                {lastHb == null
                  ? `Presence check will run every ${policy.presence_check_interval_minutes} min`
                  : lastHb.inside_geofence
                    ? `Inside ${lastHb.center_name || "geofence"} · last ping ${format(parseISO(lastHb.timestamp), "HH:mm")}`
                    : `Outside geofence · ${Math.round(lastHb.distance_meters || 0)}m at ${format(parseISO(lastHb.timestamp), "HH:mm")}`}
              </span>
            </div>
            <span className="label-mono shrink-0 ml-2">{policy.presence_check_interval_minutes}m</span>
          </div>
        )}

        {(today?.presence_breaches || 0) > 0 && (
          <div className="mt-2 text-[11px] text-orange-300 font-mono-brand">
            {today.presence_breaches} presence breach{today.presence_breaches === 1 ? "" : "es"} recorded today.
          </div>
        )}
      </Card>
    </div>
  );
}
